import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiAxios from "helpers/apiAxios";
import { toast } from "react-toastify";
import {
  initialState,
  handleFulfilled,
  setNestedPageHelper,
  setNestedPageSizeHelper,
  setNestedParamsHelper,
  resetNestedParamsHelper,
  setSortParamsHelper,
  setFilterTagHelper,
  resetFilterTagHelper,
  handlePayload
} from 'helpers/reducerHelpers';

const featureFlagUrl = "/authorization/feature_flags";

export const getAllFeatureFlags = createAsyncThunk(
  "FeatureFlag/getAllFeatureFlags",
  async (params) => {
    try {
      const response = await apiAxios.get(`${featureFlagUrl}`, { params });
      return response.data;
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

export const createFeatureFlag = createAsyncThunk(
  "FeatureFlag/createFeatureFlag",
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(featureFlagUrl, { feature_flag: data });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateFeatureFlag = createAsyncThunk(
  "FeatureFlag/updateFeatureFlag",
  async ({ featureFlagId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.put(`${featureFlagUrl}/${featureFlagId}`, { feature_flag: data });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Deletes each flag on its own: the server may refuse one (e.g. a code with no
// permission row, which would hide the feature from everyone) without blocking the rest.
// Payload: { deleted: [id], errors: [message] } — the server's message, not axios's.
export const deleteFeatureFlag = createAsyncThunk(
  "FeatureFlag/deleteFeatureFlag",
  async (featureFlagIds) => {
    const results = await Promise.allSettled(
      featureFlagIds.map((id) => apiAxios.delete(`${featureFlagUrl}/${id}`).then(() => id))
    );
    return {
      deleted: results.filter((r) => r.status === "fulfilled").map((r) => r.value),
      errors: results.filter((r) => r.status === "rejected")
        .map((r) => r.reason?.response?.data?.error || r.reason?.message || "Could not delete the feature flag."),
    };
  }
);

const isRejectedAction = (action) => action.type.startsWith('FeatureFlag/') && action.type.endsWith('/rejected');

const featureFlagSlice = createSlice({
  name: "authorization_feature_flag",
  initialState: {
    ...initialState
  },
  reducers: {
    setNestedPage: setNestedPageHelper,
    setNestedPageSize: setNestedPageSizeHelper,
    setNestedParams: setNestedParamsHelper,
    resetNestedParams: resetNestedParamsHelper,
    setSortParams: setSortParamsHelper,
    setFilterTag: setFilterTagHelper,
    resetFilterTag: resetFilterTagHelper
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllFeatureFlags.fulfilled, (state, action) => handleFulfilled(state, action, "allData"))

      .addCase(createFeatureFlag.fulfilled, (state, action) => {
        handlePayload(state, action, "create");
      })
      .addCase(updateFeatureFlag.fulfilled, (state, action) => {
        handlePayload(state, action);
      })
      .addCase(deleteFeatureFlag.fulfilled, (state, action) => {
        const { deleted, errors } = action.payload;
        const removed = (state.allData.data || []).filter((flag) => deleted.includes(flag.id));
        state.allData.data = (state.allData.data || []).filter((flag) => !deleted.includes(flag.id));
        removed.forEach((flag) => toast.success(`Feature flag ${flag.code} was deleted.`));
        errors.forEach((message) => toast.error(message, { autoClose: 10000 }));
      })
      .addCase(deleteFeatureFlag.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addMatcher(
        isRejectedAction,
        (state, action) => {
          state.loading = false;
          toast.error(action.payload?.error || action?.error?.message);
        }
      );
  }
});

export const {
  setNestedPage,
  setNestedPageSize,
  setNestedParams,
  resetNestedParams,
  setSortParams,
  setFilterTag,
  resetFilterTag
} = featureFlagSlice.actions;

export default featureFlagSlice.reducer;
